import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { IconDownload } from '@tabler/icons-react';

import { NetworkStatus, useMutation, useQuery } from '@apollo/client';

import { Button } from 'ui-kit';

import { formatCurrency } from 'configs/columns';

import {
  RunBidOptimization,
  RunBidOptimizationVariables,
} from 'mutation/__generated__/runBidOptimization';
import { RUN_BID_OPTIMIZATION } from 'mutation/runBidOptimization';

import { SaleBidReport, SaleBidReportVariables } from 'query/__generated__/saleBidReport';
import { SALE_BID_REPORT_QUERY } from 'query/saleBidReport';

import { firstGraphQLError } from 'utils/apolloGraphQLError';

import {
  BidReportSummary,
  BidReportUnreadableError,
  downloadBase64File,
  readBidReport,
} from './bidReportWorkbook';
import { formatDateTime } from './salesUtils';

type Round = { round: number; deadline_at: string };

// api's copy for loan-service's ErrNoRun (api src/graphql/resolvers/saleErrors.ts): the one
// NotFound that means "nothing has run yet" rather than a missing sale or round.
export const NO_RUN_MESSAGE = 'This round has no bid optimization run at that version.';

export const SEALED_RECHECK_MS = 30_000;

const HEADING_ID = 'sale-bid-optimization-heading';
const STATUS_ID = 'sale-bid-optimization-status';
const CONFIRM_ID = 'sale-bid-optimization-confirm-copy';

// setTimeout fires at once past a signed 32-bit delay.
const MAX_TIMEOUT_MS = 2_147_483_647;

// ui-kit's primary fill and secondary text are brand-400, under 3:1 against the light theme's
// gray-100 card.
const PRIMARY_BUTTON_LIGHT_THEME =
  'light:border-pink-700 light:bg-pink-700 light:text-white light:hover:border-pink-800 light:hover:bg-pink-800';
const SECONDARY_BUTTON_LIGHT_THEME =
  'light:border-pink-700 light:text-pink-700 light:hover:border-pink-800 light:hover:text-pink-800';
// ui-kit's buttons set outline-none.
const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white light:focus-visible:outline-pink-700';

// The optimizer reports its version as a 64-character digest; the workbook keeps it whole.
const OPTIMIZER_VERSION_SHOWN_CHARS = 12;

const MUTED_TEXT = 'text-sm text-gray-400 light:text-gray-700';
const ERROR_TEXT = 'text-sm text-status-error light:text-error-muted';

// The latest round whose deadline has passed: the one there is something to optimize in. Before
// any has closed, the first, which the panel shows as not yet runnable.
export function optimizationRound(rounds: Round[], now: number): Round | undefined {
  const closed = rounds.filter((round) => Date.parse(round.deadline_at) <= now);
  return closed[closed.length - 1] ?? rounds[0];
}

function useClockReadAfterEachDeadline(rounds: Round[]): number {
  const [now, setNow] = useState(Date.now);
  const nextDeadline = Math.min(
    ...rounds.map((round) => Date.parse(round.deadline_at)).filter((deadline) => deadline > now),
  );
  useEffect(() => {
    if (!Number.isFinite(nextDeadline)) {
      return undefined;
    }
    const wait = nextDeadline - Date.now();
    const timer = setTimeout(() => setNow(Date.now()), Math.min(wait + 1000, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [nextDeadline, now]);
  return now;
}

const runFailureCopy = (err: unknown): string =>
  firstGraphQLError(err)?.message ??
  'The optimization request failed before an answer came back. Reload the page to see whether a run was stored before you run it again.';

const pluralize = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

const confirmCopy = (round: number, hasRun: boolean, latestVersion: number | undefined) => {
  if (!hasRun) {
    return `Run the bid optimizer on round ${round}'s cash bids? The run is stored, and the bid report is generated from it.`;
  }
  const earlier = latestVersion === undefined ? 'Earlier runs stay' : `Run ${latestVersion} stays`;
  return `Run the bid optimizer on round ${round}'s cash bids again? The new run is stored as the round's latest, and the bid report then shows it. ${earlier} stored.`;
};

const Amount: FC<{ cents: number | undefined; testId: string }> = ({ cents, testId }) => (
  <span data-testid={testId} className="font-mono">
    {cents === undefined ? '-' : formatCurrency(cents)}
  </span>
);

const SummaryTile: FC<{ label: string; detail?: string; children: ReactNode; testId: string }> = ({
  label,
  detail,
  children,
  testId,
}) => (
  <div
    data-testid={testId}
    className="flex flex-col gap-1 rounded border border-slate-600 bg-background-surface p-3 light:border-gray-300 light:bg-white"
  >
    <div className="text-xs text-gray-400 light:text-gray-700">{label}</div>
    <div className="text-lg font-bold text-white light:text-gray-900">{children}</div>
    {detail && <div className="text-xs text-gray-400 light:text-gray-700">{detail}</div>}
  </div>
);

const HEADER_CELL =
  'border-b border-slate-600 px-3 py-2 text-left text-xs font-medium text-gray-400 light:border-gray-300 light:text-gray-700';
const BODY_CELL =
  'border-b border-slate-700 px-3 py-2 align-top text-sm text-white light:border-gray-200 light:text-gray-900';

const BidOptimizationResult: FC<{ summary: BidReportSummary }> = ({ summary }) => {
  const poolLabel = (poolNumber: string) => {
    const description = summary.poolDescriptions.get(poolNumber);
    return description ? `${poolNumber} · ${description}` : poolNumber;
  };
  const hasLinkedAward = summary.awards.some((award) => award.poolNumbers.length > 1);
  const linkedGainCents =
    summary.recommendedCents !== undefined && summary.bestIndividual
      ? summary.recommendedCents - summary.bestIndividual.totalCents
      : 0;
  const poolCount = summary.poolNumbers.length;

  if (summary.awards.length === 0) {
    return (
      <p data-testid="sale-bid-optimization-no-awards" className={`mt-4 ${MUTED_TEXT}`}>
        The optimizer recommended no awards in run {summary.version ?? '-'}.
      </p>
    );
  }

  return (
    <div data-testid="sale-bid-optimization-result" className="mt-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryTile
          label="Recommended combination"
          detail={`${pluralize(poolCount - summary.unawardedPoolNumbers.length, 'pool')} of ${poolCount} recommended`}
          testId="sale-bid-optimization-recommended"
        >
          <Amount
            cents={summary.recommendedCents}
            testId="sale-bid-optimization-recommended-value"
          />
        </SummaryTile>
        <SummaryTile
          label="Best individual bids"
          detail={
            summary.bestIndividual
              ? `Highest individual bid on each pool · ${summary.bestIndividual.poolCount} of ${poolCount} pools`
              : 'No individual bids'
          }
          testId="sale-bid-optimization-best-individual"
        >
          <Amount
            cents={summary.bestIndividual?.totalCents}
            testId="sale-bid-optimization-best-individual-value"
          />
        </SummaryTile>
        <SummaryTile
          label="Sale-level cover"
          detail={
            summary.saleLevelCover?.excludedBidder
              ? `Best combination with no bid from ${summary.saleLevelCover.excludedBidder}`
              : 'None'
          }
          testId="sale-bid-optimization-cover"
        >
          <Amount
            cents={summary.saleLevelCover?.totalCents}
            testId="sale-bid-optimization-cover-value"
          />
        </SummaryTile>
      </div>

      {hasLinkedAward && linkedGainCents > 0 && (
        <p
          data-testid="sale-bid-optimization-linked-gain"
          className="mt-3 text-sm font-medium text-success-default light:text-success-muted"
        >
          Linked bids increased recovery by {formatCurrency(linkedGainCents)} over the best
          individual bids.
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table data-testid="sale-bid-optimization-awards" className="w-full border-collapse">
          <caption className="mb-2 text-left text-sm font-medium text-white light:text-gray-900">
            Recommended awards, run {summary.version ?? '-'}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={HEADER_CELL}>
                Bid #
              </th>
              <th scope="col" className={HEADER_CELL}>
                Bidder
              </th>
              <th scope="col" className={HEADER_CELL}>
                Pools
              </th>
              <th scope="col" className={HEADER_CELL}>
                Bid
              </th>
              <th scope="col" className={`${HEADER_CELL} text-right`}>
                Recommended
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.awards.map((award) => (
              <tr key={award.bidNumber} data-testid="sale-bid-optimization-award">
                <td className={BODY_CELL}>{award.bidNumber}</td>
                <td className={BODY_CELL}>{award.bidder}</td>
                <td className={BODY_CELL}>
                  {award.poolNumbers.map((poolNumber) => (
                    <div key={poolNumber} className="whitespace-nowrap">
                      {poolLabel(poolNumber)}
                    </div>
                  ))}
                </td>
                <td className={BODY_CELL}>
                  {award.poolNumbers.length > 1
                    ? `Linked, ${award.poolNumbers.length} pools`
                    : 'Individual'}
                </td>
                <td className={`${BODY_CELL} text-right`}>
                  <Amount cents={award.amountCents} testId="sale-bid-optimization-award-amount" />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th
                scope="row"
                colSpan={4}
                className="px-3 py-2 text-left text-sm font-medium text-white light:text-gray-900"
              >
                Recommended combination
              </th>
              <td className="px-3 py-2 text-right text-sm font-bold text-white light:text-gray-900">
                <Amount cents={summary.recommendedCents} testId="sale-bid-optimization-total" />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {summary.unawardedPoolNumbers.length > 0 && (
        <p data-testid="sale-bid-optimization-unawarded" className={`mt-3 ${MUTED_TEXT}`}>
          No recommended award: {summary.unawardedPoolNumbers.map(poolLabel).join(', ')}.
        </p>
      )}
      {summary.excludedBidderCount > 0 && (
        <p data-testid="sale-bid-optimization-excluded" className={`mt-3 ${MUTED_TEXT}`}>
          {pluralize(summary.excludedBidderCount, 'bidder was', 'bidders were')} left out: not
          approved when the run read the bids. The report's Excluded Bidders sheet lists them.
        </p>
      )}
      <p
        data-testid="sale-bid-optimization-run"
        className="mt-3 text-xs text-gray-400 light:text-gray-700"
      >
        Run {summary.version ?? '-'} of round {summary.round ?? '-'}
        {summary.optimizerVersion && (
          <>
            {' · optimizer '}
            <span title={summary.optimizerVersion}>
              {summary.optimizerVersion.slice(0, OPTIMIZER_VERSION_SHOWN_CHARS)}
            </span>
          </>
        )}{' '}
        · {pluralize(summary.individualBidCount + summary.linkedBidCount, 'bid')}:{' '}
        {summary.individualBidCount} individual, {summary.linkedBidCount} linked
      </p>
    </div>
  );
};

// Inline rather than ui-kit's ConfirmDialog, whose focus manager is handed a context without the
// dialog's element, so keyboard focus never enters it.
const RunConfirmation: FC<{ copy: string; onConfirm: () => void; onCancel: () => void }> = ({
  copy,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);
  return (
    <div
      role="group"
      aria-labelledby={CONFIRM_ID}
      data-testid="sale-bid-optimization-confirm"
      className="mt-3 rounded border border-pink-400 bg-background-surface p-3 light:border-pink-700 light:bg-white"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onCancel();
        }
      }}
    >
      <p id={CONFIRM_ID} className="text-sm text-white light:text-gray-900">
        {copy}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          ref={confirmRef}
          size="small"
          type="primary"
          label="Run optimization"
          data-testid="sale-bid-optimization-confirm-run"
          className={`${FOCUS_RING} ${PRIMARY_BUTTON_LIGHT_THEME}`}
          onClick={onConfirm}
        >
          Run optimization
        </Button>
        <Button
          size="small"
          type="secondary"
          label="Cancel"
          data-testid="sale-bid-optimization-confirm-cancel"
          className={`${FOCUS_RING} ${SECONDARY_BUTTON_LIGHT_THEME}`}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const RoundBidOptimization: FC<{
  saleId: string;
  round: number;
  deadlineAt: string;
  deadlinePassed: boolean;
}> = ({ saleId, round, deadlineAt, deadlinePassed }) => {
  const runButtonRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [runFailure, setRunFailure] = useState<string | null>(null);
  const [storedVersion, setStoredVersion] = useState<number | null>(null);

  const reportQuery = useQuery<SaleBidReport, SaleBidReportVariables>(SALE_BID_REPORT_QUERY, {
    variables: { saleId, round },
    skip: !deadlinePassed,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const [runOptimization, { loading: running }] = useMutation<
    RunBidOptimization,
    RunBidOptimizationVariables
  >(RUN_BID_OPTIMIZATION);

  const report = reportQuery.data?.saleBidReport ?? undefined;
  const reportError = report ? undefined : reportQuery.error;
  const reportErrorClass = firstGraphQLError(reportError)?.extensions?.errorClass;
  const sealed = reportErrorClass === 'Sealed';
  const noRunYet =
    reportErrorClass === 'NotFound' && firstGraphQLError(reportError)?.message === NO_RUN_MESSAGE;
  const loadFailure = reportError && !sealed && !noRunYet ? reportError : undefined;
  const firstLoad = deadlinePassed && reportQuery.networkStatus === NetworkStatus.loading;
  const checking = deadlinePassed && reportQuery.loading;

  const { refetch } = reportQuery;
  useEffect(() => {
    if (!sealed) {
      return undefined;
    }
    const timer = setInterval(() => {
      void refetch().catch(() => undefined);
    }, SEALED_RECHECK_MS);
    return () => clearInterval(timer);
  }, [sealed, refetch]);

  const summary = useMemo(() => {
    if (!report) {
      return undefined;
    }
    try {
      return readBidReport(report.content_base64);
    } catch (err) {
      if (err instanceof BidReportUnreadableError) {
        return err;
      }
      throw err;
    }
  }, [report]);
  const latestVersion = summary instanceof BidReportUnreadableError ? undefined : summary?.version;

  const canRun = deadlinePassed && !sealed && !checking && !running && !confirming;

  const handleRun = async () => {
    setRunFailure(null);
    setStoredVersion(null);
    let version: number | undefined;
    try {
      const { data } = await runOptimization({ variables: { saleId, round } });
      version = data?.runBidOptimization?.version;
    } catch (err) {
      setRunFailure(runFailureCopy(err));
      return;
    }
    if (version !== undefined) {
      setStoredVersion(version);
    }
    await refetch({ saleId, round, version }).catch(() => undefined);
  };

  const status = (() => {
    if (!deadlinePassed) {
      return `Bids stay sealed until round ${round} closes at ${formatDateTime(deadlineAt)}. The optimization can run once the round's bids unseal.`;
    }
    if (running) {
      return 'Running the optimization…';
    }
    if (firstLoad) {
      return 'Checking for a stored run…';
    }
    if (sealed) {
      return `Round ${round} closed at ${formatDateTime(deadlineAt)}. Its bids unseal after the grace period and unseal delay; this checks again every 30 seconds.`;
    }
    if (noRunYet) {
      return `Round ${round}'s bids are unsealed. No optimization has run yet.`;
    }
    return undefined;
  })();

  return (
    <section
      aria-labelledby={HEADING_ID}
      data-testid="sale-bid-optimization"
      className="mt-6 rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-gray-100"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            ref={headingRef}
            id={HEADING_ID}
            tabIndex={-1}
            className="font-heebo text-lg font-medium text-white focus:outline-none light:text-gray-900"
          >
            Bid optimization
          </h2>
          <p className={`mt-1 ${MUTED_TEXT}`}>
            Round {round}: the combination of cash bids that recovers the most, and its Exhibit 16
            bid day reports.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="small"
            type="primary"
            label="Run bid optimization"
            data-testid="sale-run-bid-optimization"
            ref={runButtonRef}
            disabled={!canRun}
            aria-describedby={status ? STATUS_ID : undefined}
            className={`${FOCUS_RING} ${canRun ? PRIMARY_BUTTON_LIGHT_THEME : ''}`}
            onClick={() => setConfirming(true)}
          >
            {running ? 'Running…' : 'Run bid optimization'}
          </Button>
          {report && (
            <Button
              size="small"
              type="secondary"
              label="Download bid report (.xlsx)"
              data-testid="sale-download-bid-report"
              className={`${FOCUS_RING} ${SECONDARY_BUTTON_LIGHT_THEME}`}
              onClick={() =>
                downloadBase64File(report.file_name, report.content_type, report.content_base64)
              }
            >
              <IconDownload aria-hidden="true" size={16} stroke={1.75} />
              Download bid report (.xlsx)
            </Button>
          )}
        </div>
      </div>

      {status && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p id={STATUS_ID} data-testid="sale-bid-optimization-status" className={MUTED_TEXT}>
            {status}
          </p>
          {sealed && (
            <Button
              size="small"
              type="secondary"
              label="Check again"
              data-testid="sale-bid-optimization-recheck"
              className={`${FOCUS_RING} ${SECONDARY_BUTTON_LIGHT_THEME}`}
              onClick={() => {
                void refetch().catch(() => undefined);
              }}
            >
              Check again
            </Button>
          )}
        </div>
      )}

      {confirming && (
        <RunConfirmation
          copy={confirmCopy(round, !!report, latestVersion)}
          onConfirm={() => {
            setConfirming(false);
            headingRef.current?.focus();
            void handleRun();
          }}
          onCancel={() => {
            setConfirming(false);
            setTimeout(() => runButtonRef.current?.focus());
          }}
        />
      )}

      {runFailure && (
        <p
          role="alert"
          data-testid="sale-bid-optimization-run-failed"
          className={`mt-3 ${ERROR_TEXT}`}
        >
          {runFailure}
        </p>
      )}

      {loadFailure && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p role="alert" data-testid="sale-bid-optimization-load-failed" className={ERROR_TEXT}>
            Couldn't load the latest run:{' '}
            {firstGraphQLError(loadFailure)?.message ?? loadFailure.message}
          </p>
          <Button
            size="small"
            type="secondary"
            label="Retry"
            data-testid="sale-bid-optimization-retry"
            className={`${FOCUS_RING} ${SECONDARY_BUTTON_LIGHT_THEME}`}
            onClick={() => {
              void refetch().catch(() => undefined);
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {storedVersion !== null && !runFailure && (
        <p
          role="status"
          data-testid="sale-bid-optimization-stored"
          className="mt-3 text-sm text-success-default light:text-success-muted"
        >
          Run {storedVersion} stored.
        </p>
      )}

      {summary instanceof BidReportUnreadableError ? (
        <p data-testid="sale-bid-optimization-unreadable" className={`mt-3 ${MUTED_TEXT}`}>
          This run's bid report can't be summarised here. Download it to read it.
        </p>
      ) : (
        summary && <BidOptimizationResult summary={summary} />
      )}
    </section>
  );
};

// The seller's bid optimization of the sale's latest closed round: running it, its stored result
// and the Exhibit 16 workbook.
export const SaleBidOptimizationPanel: FC<{ saleId: string; rounds: Round[] }> = ({
  saleId,
  rounds,
}) => {
  const now = useClockReadAfterEachDeadline(rounds);
  const round = optimizationRound(rounds, now);
  return round ? (
    <RoundBidOptimization
      key={`${saleId}:${round.round}`}
      saleId={saleId}
      round={round.round}
      deadlineAt={round.deadline_at}
      deadlinePassed={Date.parse(round.deadline_at) <= now}
    />
  ) : null;
};
